import { ActionContext, Store, Module } from 'vuex'
import { Namespace } from '@/store/Namespaces'
import {
  qRAuthenticate,
  qRAuthCancel,
  STATUS_FAILED,
  STATUS_PENDING,
  qRCollect,
  getJwt
} from '@/services/authentication.v6.service'
import { Properties } from '@/types/authentication.types'
import { parseJwtPayload } from '@/utils/authentication.utils'
import { SESSION_EXPIRATION_MESSAGE } from '@/constants/landlord/order-property'

export enum Setters {
  SetFullName = 'SetFullName',
  SetPersonalIdentityNumber = 'SetPersonalIdentityNumber',
  SetIsAuthenticationOngoing = 'SetIsAuthenticationOngoing',
  SetIsAuthenticated = 'SetIsAuthenticated',
  SetAuthenticationError = 'SetAuthenticationError',
  SetAuthenticationWarning = 'SetAuthenticationWarning',
  SetIsCancellationOngoing = 'SetIsCancellationOngoing',
  SetToken = 'SetToken',
  SetJwt = 'SetJwt',
  SetQrToken = 'SetQrToken',
  SetAutoStartToken = 'SetAutoStartToken',
  SetStartTime = 'SetStartTime'
}

interface AuthenticationState {
  [Properties.FullName]: string;
  [Properties.PersonalIdentityNumber]: string;
  [Properties.IsAuthenticated]: boolean;
  [Properties.IsAuthenticationOngoing]: boolean;
  [Properties.AuthenticationError]: Error | null;
  [Properties.AuthenticationWarning]: string | null;
  [Properties.IsCancellationOngoing]: boolean;
  [Properties.AutoStartToken]: string | null;
  [Properties.QrToken]: string | null;
  [Properties.Token]: string;
  [Properties.Jwt]: string;
  [Properties.StartTime]: number | null;
}

const ERROR_MESSAGE_TRANSACTION_IS_CANCELED = 'Transaktionen är avbruten'
const ERROR_MESSAGE_AUTHENTICATION_FAILED = 'BankID-autentisering misslyckades av tekniska skäl'
const ERROR_MESSAGE_AUTHENTICATION_TIMEOUT = 'BankID-autentisering tog för lång tid. Försök igen.'

const TIMEOUT_DURATION = 3 * 60 * 1000

export type VuexActionContext = ActionContext<AuthenticationState, {}>;
type AuthenticationStore = Store<AuthenticationState>;

export const namespace = (s: string): string => `${Namespace.AuthenticationV6}/${s}`

export const getIsAuthenticated = (store: AuthenticationStore): boolean => store.getters[namespace(Properties.IsAuthenticated)]
export const getIsAuthenticationOngoing = (store: AuthenticationStore): boolean => store.getters[namespace(Properties.IsAuthenticationOngoing)]
export const getAuthenticationError = (store: AuthenticationStore): string | null => store.getters[namespace(Properties.AuthenticationError)]
export const getAuthenticationWarning = (store: AuthenticationStore): string | null => store.getters[namespace(Properties.AuthenticationWarning)]
export const getIsCancellationOngoing = (store: AuthenticationStore): boolean => store.getters[namespace(Properties.IsCancellationOngoing)]
export const getFullName = (store: AuthenticationStore): string => store.getters[namespace(Properties.FullName)]
export const getPersonalIdentityNumber = (store: AuthenticationStore): string => store.getters[namespace(Properties.PersonalIdentityNumber)]

export const getToken = (store: AuthenticationStore): string => store.getters[namespace(Properties.Token)]
export const getQrToken = (store: AuthenticationStore): string => store.getters[namespace(Properties.QrToken)]
export const getAutoStartToken = (store: AuthenticationStore): string => store.getters[namespace(Properties.AutoStartToken)]

export const setAuthenticationError = (store: AuthenticationStore, authenticationError: Error | null): void => store.commit(namespace(Setters.SetAuthenticationError), authenticationError)
export const setAuthenticationWarning = (store: AuthenticationStore, authenticationWarning: string | null): void => store.commit(namespace(Setters.SetAuthenticationWarning), authenticationWarning)

export const requestAuthentication = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.RequestAuthentication))
export const collectStatus = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.CollectStatus))
export const authenticate = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.Authenticate))
export const cancelAuthentication = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.CancelAuthentication))
export const initAuthStore = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.Init))
export const expireSession = (store: AuthenticationStore): Promise<void> => store.dispatch(namespace(Properties.ExpireSession))

const AuthenticationV6Store: Module<AuthenticationState, {}> = {
  namespaced: true,
  state: {
    [Properties.FullName]: '',
    [Properties.PersonalIdentityNumber]: '',
    [Properties.IsAuthenticated]: false,
    [Properties.IsAuthenticationOngoing]: false,
    [Properties.IsCancellationOngoing]: false,
    [Properties.AuthenticationError]: null,
    [Properties.AuthenticationWarning]: null,
    [Properties.Token]: '',
    [Properties.Jwt]: '',
    [Properties.QrToken]: '',
    [Properties.AutoStartToken]: '',
    [Properties.StartTime]: null
  },
  getters: {
    [Properties.FullName]: (state: AuthenticationState) => state.fullName,
    [Properties.PersonalIdentityNumber]: (state: AuthenticationState) => state.personalIdentityNumber,
    [Properties.IsAuthenticated]: (state: AuthenticationState) => state.isAuthenticated,
    [Properties.IsAuthenticationOngoing]: (state: AuthenticationState) => state.isAuthenticationOngoing,
    [Properties.IsCancellationOngoing]: (state: AuthenticationState) => state.isCancellationOngoing,
    [Properties.AuthenticationError]: (state: AuthenticationState) => state.authenticationError,
    [Properties.AuthenticationWarning]: (state: AuthenticationState) => state.authenticationWarning,
    [Properties.Token]: (state: AuthenticationState) => state.token,
    [Properties.Jwt]: (state: AuthenticationState) => state.jwt,
    [Properties.QrToken]: (state: AuthenticationState) => state.qrToken,
    [Properties.AutoStartToken]: (state: AuthenticationState) => state.autoStartToken
  },
  mutations: {
    [Setters.SetFullName]: (state: AuthenticationState, fullName: string) => { state.fullName = fullName },
    [Setters.SetPersonalIdentityNumber]: (state: AuthenticationState, personalIdentityNumber: string) => { state.personalIdentityNumber = personalIdentityNumber },
    [Setters.SetIsAuthenticated]: (state: AuthenticationState, isAuthenticated: boolean) => { state.isAuthenticated = isAuthenticated },
    [Setters.SetIsAuthenticationOngoing]: (state: AuthenticationState, isAuthenticationOngoing: boolean) => { state.isAuthenticationOngoing = isAuthenticationOngoing },
    [Setters.SetIsCancellationOngoing]: (state: AuthenticationState, isCancellationOngoing: boolean) => { state.isCancellationOngoing = isCancellationOngoing },
    [Setters.SetAuthenticationError]: (state: AuthenticationState, authenticationError: Error | null) => { state.authenticationError = authenticationError },
    [Setters.SetAuthenticationWarning]: (state: AuthenticationState, authenticationWarning: string | null) => { state.authenticationWarning = authenticationWarning },
    [Setters.SetToken]: (state: AuthenticationState, token: string) => { state.token = token },
    [Setters.SetJwt]: (state: AuthenticationState, jwt: string) => { state.jwt = jwt },
    [Setters.SetQrToken]: (state: AuthenticationState, qrToken: string) => { state.qrToken = qrToken },
    [Setters.SetAutoStartToken]: (state: AuthenticationState, autoStartToken: string) => { state.autoStartToken = autoStartToken },
    [Setters.SetStartTime]: (state: AuthenticationState, startTime: number) => { state.startTime = startTime }
  },
  actions: {
    [Properties.Authenticate]: ({ commit }: ActionContext<AuthenticationState, {}>, jwt: string): void => {
      commit(Setters.SetAuthenticationError, null)
      commit(Setters.SetAuthenticationWarning, null)

      const userData = parseJwtPayload(jwt)
      commit(Setters.SetFullName, userData.name)
      commit(Setters.SetPersonalIdentityNumber, userData.personal_number)
      commit(Setters.SetJwt, jwt)

      commit(Setters.SetIsAuthenticated, true)
      commit(Setters.SetIsAuthenticationOngoing, false)
    },
    [Properties.CancelAuthentication]: async ({ commit, dispatch, state }: ActionContext<AuthenticationState, {}>): Promise<void> => {
      try {
        commit(Setters.SetIsCancellationOngoing, true)
        await qRAuthCancel(state.token)
        commit(Setters.SetIsAuthenticated, false)
        await dispatch(Properties.EndSession)
        commit(Setters.SetAuthenticationWarning, ERROR_MESSAGE_TRANSACTION_IS_CANCELED)
      } catch (err) {
        commit(Setters.SetAuthenticationError, err)
      }
    },
    [Properties.CollectStatus]: async ({ commit, state, dispatch }: ActionContext<AuthenticationState, {}>): Promise<void> => {
      commit(Setters.SetAuthenticationError, null)
      commit(Setters.SetAuthenticationWarning, null)
      commit(Setters.SetIsAuthenticationOngoing, true)

      if (state.startTime === null) {
        commit(Setters.SetStartTime, Date.now())
      }

      const collectQrAndStatusAttempt = async (): Promise<void> => {

        try {
          if (Date.now() - (state.startTime || 0) > TIMEOUT_DURATION) {
            throw new Error('Timeout')
          }

          const data = await qRCollect(state.token)
          commit(Setters.SetQrToken, data.qrToken)

          if (data.status === STATUS_PENDING) {
            await new Promise(resolve => setTimeout(resolve, 1000))
            await collectQrAndStatusAttempt()
          } else if (data.status === STATUS_FAILED) {
            await dispatch(Properties.RequestAuthentication)
          } else {
            await dispatch(Properties.Authenticate, data.token)
          }
        } catch (error) {
          if (error instanceof Error && error.message === 'Timeout') {
            await dispatch(Properties.CancelAuthentication)
            commit(Setters.SetAuthenticationError, ERROR_MESSAGE_AUTHENTICATION_TIMEOUT)
          } else {
            await dispatch(Properties.EndSession)
            commit(Setters.SetAuthenticationError, ERROR_MESSAGE_AUTHENTICATION_FAILED)

          }
        }
      }

      collectQrAndStatusAttempt()
    },
    [Properties.EndSession]: ({ commit }: ActionContext<AuthenticationState, {}>): void => {
      commit(Setters.SetToken, '')
      commit(Setters.SetFullName, '')
      commit(Setters.SetPersonalIdentityNumber, '')
      commit(Setters.SetJwt, '')
      commit(Setters.SetQrToken, '')
      commit(Setters.SetAutoStartToken, '')
      commit(Setters.SetAuthenticationError, null)
      commit(Setters.SetAuthenticationWarning, null)
      commit(Setters.SetIsAuthenticationOngoing, false)
      commit(Setters.SetIsCancellationOngoing, false)
      commit(Setters.SetIsAuthenticated, false)
      commit(Setters.SetStartTime, null)
    },
    [Properties.Init]: async ({ dispatch }: ActionContext<AuthenticationState, {}>): Promise<void> => {
      try {
        const { token } = await getJwt()
        await dispatch(Properties.Authenticate, token)
      } catch {
        await dispatch(Properties.EndSession)
      }
    },
    [Properties.RequestAuthentication]: async ({ commit, dispatch }: ActionContext<AuthenticationState, {}>): Promise<void> => {
      try {
        commit(Setters.SetAuthenticationError, null)
        commit(Setters.SetAuthenticationWarning, null)
        commit(Setters.SetIsAuthenticationOngoing, true)

        const data = await qRAuthenticate()

        commit(Setters.SetToken, data.token)
        commit(Setters.SetAutoStartToken, data.autoStartToken)

        await dispatch(Properties.CollectStatus)
      } catch {
        commit(Setters.SetAuthenticationError, ERROR_MESSAGE_AUTHENTICATION_FAILED)
        commit(Setters.SetIsAuthenticationOngoing, false)
      }
    },
    [Properties.ExpireSession]: async ({ commit, dispatch }: ActionContext<AuthenticationState, {}>): Promise<void> => {
      await dispatch(Properties.EndSession)
      commit(Setters.SetAuthenticationWarning, SESSION_EXPIRATION_MESSAGE)
    }
  }
}

export default AuthenticationV6Store
